import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getCrops } from '@next/arable-lib/crop';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AgroConnectCropField,
  ChangedCropFields,
  CropField,
  ImportCropfieldsSelection,
  SiteState,
  SyncStatusEnum,
} from 'src/app/shared/models';

import { isRvOProvider } from '../../../shared/helpers/is-rvo-provider';
import { LoadCropVarietiesByCropIdsRequest } from '../../store/actions/crop-variety.actions';
import { getCropFields } from '../../store/selectors/crop-field.selectors';
import { getCropVarietiesByCropId } from '../../store/selectors/crop-variety.selectors';

export interface SyncCropfieldsDialogModel extends AbstractFormDialogData {
  newCropFields: CropField[];
  deletedCropFields: AgroConnectCropField[];
  changedCropFields: ChangedCropFields;
  year?: number;
  provider: string;
}

export interface SelectableCropField extends CropField {
  isSelected: boolean;
  cropName: string;
  cropVarietyName: string;
}

export interface SelectableCropFieldWithOldValues extends SelectableCropField {
  oldValues: SelectableCropField;
}

@Component({
  selector: 'app-sync-cropfields-dialog',
  templateUrl: './sync-cropfields-dialog.component.html',
  styleUrls: ['./sync-cropfields-dialog.component.scss'],
  standalone: false,
})
export class SyncCropfieldsDialogComponent
  extends AbstractFormDialog<ImportCropfieldsSelection>
  implements OnInit, OnDestroy
{
  data = inject<SyncCropfieldsDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        SyncCropfieldsDialogComponent,
        AbstractDialogResultData<ImportCropfieldsSelection>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  formGroup = this.fb.group({ year: [this.data.year, [Validators.required]] });

  selectableNewCropFields: SelectableCropField[] = [];
  selectableChangedCropFields: SelectableCropFieldWithOldValues[] = [];
  selectableDeletedCropFields: SelectableCropField[] = [];

  ngOnInit(): void {
    const newCropIds = this.data.newCropFields
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);

    const existingCropIds = this.data.changedCropFields.newValues
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);

    const deletedCropIds = this.data.deletedCropFields
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);

    const cropIds = [...new Set([...newCropIds, ...existingCropIds, ...deletedCropIds])];

    setTimeout(() => this.store.dispatch(new LoadCropVarietiesByCropIdsRequest(cropIds)), 0);

    this.selectableNewCropFields = [...this.data.newCropFields]
      .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1))
      .map(
        (cropField) =>
          ({
            ...cropField,
            isSelected: true,
            cropName: null,
            cropVarietyName: null,
            syncStatus: SyncStatusEnum.New,
          }) as SelectableCropField,
      );

    this.selectableChangedCropFields = [...this.data.changedCropFields.newValues]
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(
        (cropField) =>
          ({
            ...cropField,
            isSelected: true,
            cropName: null,
            cropVarietyName: null,
            syncStatus: SyncStatusEnum.Updated,
            oldValues: {
              ...this.data.changedCropFields.oldValues.find((x) => x.id === cropField.id),
              isSelected: false,
              cropName: null,
              cropVarietyName: null,
            },
          }) as SelectableCropFieldWithOldValues,
      );

    this.store.pipe(select(getCropFields), takeUntil(this.destroyed$)).subscribe((cropFields) => {
      this.selectableDeletedCropFields = [...this.data.deletedCropFields]
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
        .map((cropField) => {
          const existingCropField = cropFields.find((x) => x.id === cropField.id);
          if (existingCropField) {
            return {
              ...existingCropField,
              isSelected: true,
              cropName: null,
              cropVarietyName: null,
              externalIdSource: this.data.provider,
              syncStatus: SyncStatusEnum.NotPresent,
            } as SelectableCropField;
          }
        });
    });

    this.store.pipe(select(getCrops), takeUntil(this.destroyed$)).subscribe((crops) => {
      for (const cropField of this.selectableNewCropFields) {
        cropField.cropName = crops.find((crop) => crop.id === cropField.cropId)?.name;
      }
      for (const cropField of this.selectableChangedCropFields) {
        cropField.cropName = crops.find((crop) => crop.id === cropField.cropId)?.name;
        cropField.oldValues.cropName = crops.find(
          (crop) => crop.id === cropField.oldValues.cropId,
        )?.name;
      }
      for (const cropField of this.selectableDeletedCropFields) {
        cropField.cropName = crops.find((crop) => crop.id === cropField.cropId)?.name;
      }
    });

    this.store
      .pipe(select(getCropVarietiesByCropId), takeUntil(this.destroyed$))
      .subscribe((cropVarietiesByCropId) => {
        for (const cropField of this.selectableNewCropFields) {
          const cropVarieties = cropVarietiesByCropId.find(
            (cropVariety) => cropVariety.id === cropField.cropId,
          )?.cropVarieties;
          if (cropVarieties) {
            cropField.cropVarietyName = cropVarieties.find(
              (cropVariety) => cropVariety.id === cropField.cropVarietyId,
            )?.name;
          }
        }

        for (const cropField of this.selectableChangedCropFields) {
          const cropVarieties = cropVarietiesByCropId.find(
            (cropVariety) => cropVariety.id === cropField.cropId,
          )?.cropVarieties;
          if (cropVarieties) {
            cropField.cropVarietyName = cropVarieties.find(
              (cropVariety) => cropVariety.id === cropField.cropVarietyId,
            )?.name;
          }

          const cropVarietiesForOldValue = cropVarietiesByCropId.find(
            (cropVariety) => cropVariety.id === cropField.oldValues.cropId,
          )?.cropVarieties;
          if (cropVarietiesForOldValue) {
            cropField.oldValues.cropVarietyName = cropVarietiesForOldValue.find(
              (cropVariety) => cropVariety.id === cropField.oldValues.cropVarietyId,
            )?.name;
          }
        }

        for (const cropField of this.selectableDeletedCropFields) {
          const cropVarieties = cropVarietiesByCropId.find(
            (cropVariety) => cropVariety.id === cropField.cropId,
          )?.cropVarieties;
          if (cropVarieties) {
            cropField.cropVarietyName = cropVarieties.find(
              (cropVariety) => cropVariety.id === cropField.cropVarietyId,
            )?.name;
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  confirmSelection(result: ImportCropfieldsSelection) {
    result.cropFields = [
      ...this.selectableNewCropFields,
      ...this.selectableChangedCropFields,
      ...this.selectableDeletedCropFields,
    ]
      .filter((f) => f.isSelected)
      .map((f) => f as CropField);
    this.confirm(result);
  }

  getCropNamesFromSelectableCropFieldsWithCropFieldCounts = (
    selectableCropFields: SelectableCropField[],
  ): { [key: string]: number } =>
    selectableCropFields.reduce((acc: { [key: string]: number }, obj: SelectableCropField) => {
      const key = obj?.cropName;
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key]++;
      return acc;
    }, {});

  toggleAllCropFieldsWithCropName(cropName: string, selectableCropFields: SelectableCropField[]) {
    const isSelected = selectableCropFields.some(
      (cropField) => cropField.cropName === cropName && cropField.isSelected,
    );

    selectableCropFields = selectableCropFields.map((cropField) => {
      if (cropField.cropName === cropName) {
        cropField.isSelected = !isSelected;
      }
      return cropField;
    });
  }

  allCropFieldsOfCropTypeSelected = (
    cropName: string,
    cropFieldsOfCropTypeCount: number,
    selectableCropFields: SelectableCropField[],
  ): number =>
    selectableCropFields.filter(
      (cropField) => cropField.cropName === cropName && cropField.isSelected,
    ).length - cropFieldsOfCropTypeCount;

  providerName = (provider: string): string => {
    return isRvOProvider(provider) ? 'RvO' : 'BMS';
  };
}
